import { TZDateMini } from '@date-fns/tz';
import { useMemo } from 'react';
import {
  createIntl,
  defineMessages,
  useIntl,
  type FormatDateOptions,
  type MessageDescriptor,
} from 'react-intl';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { intlCache } from '../../forms/date-time-picker/utils/intl-cache.js';
import {
  getDayPeriodValues,
  getSafeRegionalFormat,
} from '../../forms/forms-core/time/helpers.js';
import type { Precision } from './shared.types.js';

type Options = {
  locale?: string;
  timeZone?: string;
  type?: 'datetime' | 'date' | 'time';
  precision?: Precision;
};

const placeholderMessages: Partial<
  Record<keyof Intl.DateTimeFormatPartTypesRegistry, MessageDescriptor>
> = defineMessages({
  year: {
    defaultMessage: 'YYYY',
    id: 'KgAwef2nXv2+/xgE',
    description: 'The abbreviation of the year part of a date value',
  },
  month: {
    defaultMessage: 'MM',
    id: 'Bo8Z1UaJVL3ONFo8',
    description: 'The abbreviation of the month part of a date value',
  },
  day: {
    defaultMessage: 'DD',
    id: '42g95tLLPXcqbFLl',
    description: 'The abbreviation of the day part of a date value',
  },
  hour: {
    defaultMessage: 'hh',
    id: '5+yhrNhI2AlQmDAo',
    description: 'The abbreviation of the hour part of a time value',
  },
  minute: {
    defaultMessage: 'mm',
    id: 'Xup0Ti6gaxC1KDSY',
    description: 'The abbreviation of the minute part of a time value',
  },
  second: {
    defaultMessage: 'ss',
    id: 'ktoTdX660gW5R80Z',
    description: 'The abbreviation of the second part of a time value',
  },
  fractionalSecond: {
    defaultMessage: 'sss',
    id: 'ERAhDK0Wy/n7Sd2e',
    description:
      'The abbreviation of the fractional of the second part of a time value',
  },
});

/**
 *  Helper function to get the placeholder for a given date time part type. The placeholder is used to show the expected format of the input value to the user. For example, "YYYY" for year, "MM" for month, etc.
 * @internal
 */
export function getPlaceholder(
  type: keyof Intl.DateTimeFormatPartTypesRegistry,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) {
  const messageDescriptor = placeholderMessages[type];

  if (messageDescriptor) {
    return formatMessage(messageDescriptor);
  }

  if (type === 'dayPeriod') {
    return getDayPeriodValues().join('/');
  }

  return type;
}

/**
 * Hook providing localized datetime format and placeholders for given locale and timezone. If no locale is specified, the user region  setting is used.
 * @internal
 */
export const useDateTimeMessages = (options?: Options) => {
  const {
    locale = getSafeRegionalFormat(),
    timeZone: timeZoneProp = getTimezone(),
    type = 'datetime',
    precision = 'minutes',
  }: Options = options ?? {};

  const { formatMessage } = useIntl();
  const { formatDateToParts, timeZone } = useMemo(
    () => createIntl({ locale, timeZone: timeZoneProp }, intlCache),
    [locale, timeZoneProp],
  );

  const formatOptions = useMemo(() => {
    const options: FormatDateOptions = {};
    switch (type) {
      case 'date':
        options['year'] = 'numeric';
        options['month'] = '2-digit';
        options['day'] = '2-digit';
        return options; // Return config and ignore precision prop if only the date is shown.
      case 'datetime':
        options['year'] = 'numeric';
        options['month'] = '2-digit';
        options['day'] = '2-digit';
        options['hour'] = '2-digit';
        options['minute'] = '2-digit';
        break;
      case 'time':
        options['hour'] = 'numeric';
        options['minute'] = '2-digit';
        break;
    }

    switch (precision) {
      case 'seconds':
        options['second'] = '2-digit';
        break;
      case 'milliseconds':
        options['second'] = '2-digit';
        options['fractionalSecondDigits'] = 3;
        break;
    }

    return options;
  }, [type, precision]);

  const parts = useMemo(
    () =>
      formatDateToParts(TZDateMini.tz(getTimezone()).getTime(), formatOptions),
    [formatOptions, formatDateToParts],
  );

  const formatString = useMemo(
    () =>
      parts
        .map(({ type, value }) =>
          type === 'literal' ? value : getPlaceholder(type, formatMessage),
        )
        .join(''),
    [formatMessage, parts],
  );

  return {
    locale,
    timeZone,
    formatDateToParts,
    parts,
    formatOptions,
    formatString,
    getPlaceholder,
  };
};
